import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useTenant } from "./TenantContext";
import { useToast } from "./ToastContext";
import { AppActionsContext } from "./AppActionsContext";
import { useTier } from "../hooks/useTier";
import { useSongs } from "../hooks/useSongs";
import { useWorkingSetlist } from "../hooks/useWorkingSetlist";
import { useSetlistManagement } from "../hooks/useSetlistManagement";
import { useMemberManagement } from "../hooks/useMemberManagement";
import { useInviteManagement } from "../hooks/useInviteManagement";
import { useLiveMode } from "../hooks/useLiveMode";
import { useUpgradeModal } from "../features/upgrade/useUpgradeModal";
import UpgradeModal from "../components/UpgradeModal";

const BandWorkspaceCtx = createContext(null);

export function BandWorkspaceProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    tenantContext,
    role: userRole,
    bandId: activeBandId,
    bandName: activeBandName,
    ownerId: bandOwnerId,
    isOwner,
    patchTenant,
  } = useTenant();

  const showToast = useToast();
  const tier = useTier(tenantContext);
  const { showSolo, showBand, dismiss: dismissUpgrade, currentModal: upgradeModal } = useUpgradeModal();

  const [activeTab, setActiveTab] = useState("songs");
  const [confirmModal, setConfirmModal] = useState(null);
  const requestConfirm = setConfirmModal;
  const dismissConfirm = () => setConfirmModal(null);

  const songs = useSongs(tenantContext);
  const workingSetlist = useWorkingSetlist();

  const setlists = useSetlistManagement({
    tenantContext,
    user,
    songs: songs.songs,
    selectedSongs: workingSetlist.selectedSongs,
    setSelectedSongs: workingSetlist.setSelectedSongs,
    userRole,
    tierInfo: tier,
    showSolo,
    requestConfirm,
    dismissConfirm,
    showToast,
    onNavigate: setActiveTab,
  });

  const members = useMemberManagement({
    tenantContext,
    userRole,
    user,
    activeBandName,
    requestConfirm,
    dismissConfirm,
    patchTenant,
    showToast,
  });

  const invites = useInviteManagement({
    tenantContext,
    userRole,
    user,
    activeBandId,
    showToast,
  });

  const liveMode = useLiveMode({
    songs: songs.songs,
    tenantContext,
    resolveSongPdfUrl: songs.resolveSongPdfUrl,
    showToast,
  });

  // Refresh members + invites on tab switch
  useEffect(() => {
    if (activeTab === "members") {
      members.refresh();
      invites.fetchInvites();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Post-upgrade confirmation polling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("upgraded")) return;
    window.history.replaceState({}, "", window.location.pathname);
    if (!tenantContext?.bandId) return;
    const bandRef = doc(db, "bands", tenantContext.bandId);
    const start = Date.now();
    const interval = setInterval(async () => {
      try {
        const snap = await getDoc(bandRef);
        const t = snap.data()?.tier;
        if (t && t !== "free") {
          clearInterval(interval);
          showToast(`You're now on ${t} — enjoy the upgrade!`);
        } else if (Date.now() - start >= 6000) {
          clearInterval(interval);
        }
      } catch {
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [tenantContext?.bandId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Onboarding redirect for fresh admins (only from main app route)
  useEffect(() => {
    if (location.pathname !== "/") return;
    if (!activeBandId || userRole !== "admin") return;
    if (songs.isLoading || setlists.isSetlistsLoading) return;
    if (localStorage.getItem(`bos_onboarding_complete_${activeBandId}`) === "true") return;
    if (localStorage.getItem(`bos_onboarding_dismissed_${activeBandId}`) === "true") return;
    const liveEntered = localStorage.getItem(`bos_live_mode_entered_${activeBandId}`) === "true";
    if (songs.songs.length < 3 || setlists.setlists.length === 0 || !liveEntered) {
      navigate("/welcome?returnTo=/");
    }
  }, [
    location.pathname,
    activeBandId,
    userRole,
    songs.isLoading,
    setlists.isSetlistsLoading,
    songs.songs.length,
    setlists.setlists.length,
    navigate,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Block rendering the workspace while we're still deciding whether to redirect
  // to onboarding. Without this guard the full app flashes for the duration of
  // the songs/setlists load before the useEffect above can navigate away.
  const pendingOnboardingCheck =
    location.pathname === "/" &&
    !!activeBandId &&
    userRole === "admin" &&
    (songs.isLoading || setlists.isSetlistsLoading) &&
    localStorage.getItem(`bos_onboarding_complete_${activeBandId}`) !== "true" &&
    localStorage.getItem(`bos_onboarding_dismissed_${activeBandId}`) !== "true";

  const value = {
    band: { id: activeBandId, name: activeBandName, ownerId: bandOwnerId },
    user: { uid: user?.uid, displayName: user?.displayName, role: userRole, isOwner },
    tenantContext,
    tier,
    patchBand: patchTenant,
    activeTab,
    setActiveTab,
    upgradeModal,
    showSolo,
    showBand,
    dismissUpgrade,
    confirmModal,
    requestConfirm,
    dismissConfirm,
    songs,
    workingSetlist,
    setlists,
    liveMode,
    members,
    invites,
  };

  if (pendingOnboardingCheck) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base px-6">
        <h2 className="text-sm font-medium text-text-secondary">Loading your band...</h2>
      </div>
    );
  }

  return (
    <BandWorkspaceCtx.Provider value={value}>
      <AppActionsContext.Provider value={{ startLiveModeForSetlist: liveMode.startLiveModeForSetlist }}>
        {children}
        {upgradeModal && (
          <UpgradeModal
            tier={upgradeModal}
            onClose={dismissUpgrade}
            tenantContext={tenantContext}
          />
        )}
      </AppActionsContext.Provider>
    </BandWorkspaceCtx.Provider>
  );
}

export function useBandWorkspace() {
  const ctx = useContext(BandWorkspaceCtx);
  if (!ctx) throw new Error("useBandWorkspace must be used within BandWorkspaceProvider");
  return ctx;
}
